<template>
  <div class="page-container">
    <div class="supply-demand-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner
        search-object="achievement"
        :title-main1="config.header.titleMain1"
        :title-main2="config.header.titleMain2"
        :subtitle="config.header.titleSub"
        :placeholder="config.header.searchPlaceholder"
        :button-text="config.header.searchButton"
        :keyword.sync="searchKeyword"
        @search="handleSearch"
      />

      <!-- 成果超市 -->
      <div class="market-section">
        <SectionHeader 
          :title="config.marketSection.title"
          :subtitle="config.marketSection.subTitle"
          :main-tabs="marketMainTabs.length > 0 ? marketMainTabs : (config.marketSection?.mainTabs || [])"
          :sub-tabs="marketSubTabs.length > 0 ? marketSubTabs : (config.marketSection?.subTabs || [])"
          :active-main-tab="activeMainTab"
          :active-sub-tab="activeMarketTab"
          :more-text="config.marketSection.moreText"
          @main-tab-change="handleMarketMainTabChange"
          @sub-tab-change="activeMarketTab = $event; loadAchievementList()"
          @more-click="handleMoreClick('market')"
        />

        <!-- 成果列表 -->
        <AchievementList 
          :items="achievementList"
          :transfer-type="config.marketSection.itemMeta.transferType"
          :fee-label="config.marketSection.itemMeta.feeLabel"
          :fee-value="config.marketSection.itemMeta.feeValue"
          :organization-label="config.marketSection.itemMeta.organizationLabel"
          :phone-label="config.marketSection.itemMeta.phoneLabel"
          @item-click="handleItemClick"
        />
      </div>

      <!-- 需求大厅 -->
      <div class="demand-section">
        <SectionHeader 
          :title="config.demandSection.title"
          :subtitle="config.demandSection.subTitle"
          :main-tabs="demandMainTabs.length > 0 ? demandMainTabs : (config.demandSection?.mainTabs || [])"
          :sub-tabs="demandSubTabs.length > 0 ? demandSubTabs : (config.demandSection?.subTabs || [])"
          :active-main-tab="activeDemandMainTab"
          :active-sub-tab="activeDemandTab"
          :more-text="config.demandSection.moreText"
          :extra-action-text="'揭榜挂帅'"
          :show-extra-action="true"
          @main-tab-change="handleDemandMainTabChange"
          @sub-tab-change="activeDemandTab = $event; loadDemandList()"
          @more-click="handleMoreClick('demand')"
          @extra-action-click="handleMoreClick('jiebang')"
        />

        <!-- 需求列表 -->
        <DemandList 
          :items="demandList"
          :cooperation-type="config.demandSection.itemMeta.cooperationType"
          :fee-label="config.demandSection.itemMeta.feeLabel"
          :organization-label="config.demandSection.itemMeta.organizationLabel"
          :location-label="config.demandSection.itemMeta.locationLabel"
          :deadline-label="config.demandSection.itemMeta.deadlineLabel"
          @item-click="handleItemClick"
        />
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getSupplyDemandConfig } from '@/config/supplyDemandConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import SectionHeader from '@/components/SectionHeader.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import AchievementList from '@/components/AchievementList.vue'
import DemandList from '@/components/DemandList.vue'
import supplyDemandApi from '@/api/supplyDemand'
import { getCurrentLocale } from '@/utils/i18nText'
import { reportSearchIfKeyword } from '@/utils/behaviorTrack'
import { buildSubCategoryTabsFromDict, CATEGORY_ALL_LABEL } from '@/utils/filterTabs'

export default {
  name: 'SupplyDemandCenter',
  components: {
    PageFooter,
    PageHeader,
    SectionHeader,
    SearchBanner,
    Breadcrumb,
    AchievementList,
    DemandList
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      currentMenu: 'supply',
      activeMainTab: '',
      activeMarketTab: '',
      activeDemandMainTab: '',
      activeDemandTab: '',
      // 面包屑数据
      breadcrumbItems: [],
      
      // 成果/需求共用类别字典（yAchievementTypev2）
      marketCategoryDictData: [],
      marketMainTabs: [], // 成果一级类别
      marketSubTabs: [], // 成果二级类别
      
      // 需求大厅 tabs（与成果相同的类别体系）
      demandMainTabs: [],
      demandSubTabs: [],
      // 成果列表（由接口 /achievement-api/open/achievement/market 提供）
      achievementList: [],
      
      // 需求列表（由接口 /requirement-api/open/requirement/hall 提供）
      demandList: [],
      // 揭榜挂帅入口：默认不显示，有内容才显示
      showJiebangEntry: false
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    // 设置缩放比例 - 基于1920基准尺寸等比缩放
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$el.querySelector('.supply-demand-page')
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    
    // 加载配置
    async loadConfig() {
      this.config = getSupplyDemandConfig(getCurrentLocale())
      const breadcrumb = this.config.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.current || '', path: '' }
      ]

      // 加载字典数据（成果/需求类别共用 yAchievementTypev2）
      await this.loadDictData()
      // 加载首页成果列表
      this.loadAchievementList()
      // 加载需求大厅列表
      this.loadDemandList()
      // 检查揭榜挂帅是否有数据，有数据才展示入口
      this.checkJiebangEntry()
    },
    async checkJiebangEntry() {
      try {
        const response = await supplyDemandApi.getJiebangPageList({ pageNum: 1, pageSize: 1 })
        // 兼容 axios 包装和不同后端返回结构
        const result = response?.data || response || {}
        const data = result?.data || {}
        const rows = Array.isArray(data.rows)
          ? data.rows
          : (Array.isArray(data.list) ? data.list : (Array.isArray(result.rows) ? result.rows : []))
        const total = Number(data.totalCount || data.total || result.totalCount || result.total || rows.length || 0)
        const statusOk = result.status === 0 || result.code === 0 || result.success === true
        this.showJiebangEntry = statusOk ? total > 0 : rows.length > 0
      } catch (e) {
        // 保持默认行为：失败不显示，页面表现与旧版一致
        this.showJiebangEntry = false
      }
    },

    // 加载字典数据（成果/需求类别共用 yAchievementTypev2）
    async loadDictData() {
      try {
        const dictCodeList = ['yAchievementTypev2']
        const response = await supplyDemandApi.getDictData(dictCodeList)
        const result = response.data || {}
        
        if (result.status === 0) {
          // 处理响应数据结构：{ status: 0, msg: "ok", data: { rows: [...], totalCount: 16, ... } }
          let dictData = []
          
          // 检查响应数据结构
          if (result.data && Array.isArray(result.data.rows)) {
            // 标准格式：{ status: 0, data: { rows: [...] } }
            dictData = result.data.rows
          } else if (Array.isArray(result.data)) {
            // 如果 data 直接是数组：{ status: 0, data: [...] }
            dictData = result.data
          } else if (typeof result.data === 'object' && result.data !== null) {
            // 如果是对象，将所有值展平为数组
            dictData = Object.values(result.data).flat()
          }
          
          // 确保 dictData 是数组
          if (!Array.isArray(dictData)) {
            dictData = []
          }
          
          // 处理类别（一级 / 二级，yAchievementTypev2），成果与需求共用
          const categoryDict = dictData.filter(item => item && item.dictCode === 'yAchievementTypev2')
          this.marketCategoryDictData = categoryDict
          this.buildCategoryTabs()
        } else {
          // 如果接口失败，使用测试数据
          this.loadMockDictData()
        }
      } catch (error) {
        console.error('加载字典数据失败:', error)
        // 接口异常，使用测试数据
        this.loadMockDictData()
      }
    },

    buildCategoryTabs() {
      const categoryDict = this.marketCategoryDictData || []
      const mainTypes = categoryDict
        .filter(item => item.parentCode === null || item.parentCode === '' || item.parentCode === undefined)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      const mainTabs = mainTypes.map(item => ({
        label: item.itemValue || '',
        value: item.itemCode || ''
      }))

      this.marketMainTabs = mainTabs
      this.demandMainTabs = mainTabs.slice()

      if (this.marketMainTabs.length > 0) {
        this.activeMainTab = this.marketMainTabs[0].value
        this.updateMarketSubTabs(this.activeMainTab)
      }
      if (this.demandMainTabs.length > 0) {
        this.activeDemandMainTab = this.demandMainTabs[0].value
        this.updateDemandSubTabs(this.activeDemandMainTab)
      }
    },

    buildSubTabsByMainValue(mainValue) {
      if (!mainValue) {
        return []
      }
      const subTypes = (this.marketCategoryDictData || []).filter(
        item => String(item.parentCode || '') === String(mainValue)
      )
      return buildSubCategoryTabsFromDict(subTypes, { allLabel: CATEGORY_ALL_LABEL, allValue: '' })
    },

    updateMarketSubTabs(mainValue) {
      if (!mainValue) {
        this.marketSubTabs = []
        this.activeMarketTab = ''
        return
      }
      this.marketSubTabs = this.buildSubTabsByMainValue(mainValue)
      this.activeMarketTab = this.marketSubTabs[0]?.value ?? ''
    },

    updateDemandSubTabs(mainValue) {
      if (!mainValue) {
        this.demandSubTabs = []
        this.activeDemandTab = ''
        return
      }
      this.demandSubTabs = this.buildSubTabsByMainValue(mainValue)
      this.activeDemandTab = this.demandSubTabs[0]?.value ?? ''
    },

    handleMarketMainTabChange(value) {
      this.activeMainTab = value
      this.updateMarketSubTabs(value)
      this.loadAchievementList()
    },

    handleDemandMainTabChange(value) {
      this.activeDemandMainTab = value
      this.updateDemandSubTabs(value)
      this.loadDemandList()
    },

    getMarketCategoryQueryParams() {
      const params = {}
      if (this.activeMainTab) {
        params.firstCategory = this.activeMainTab
      }
      if (this.activeMarketTab) {
        params.secondCategory = this.activeMarketTab
      }
      return params
    },

    getDemandCategoryQueryParams() {
      const params = {}
      if (this.activeDemandMainTab) {
        params.firstCategory = this.activeDemandMainTab
      }
      if (this.activeDemandTab) {
        params.secondCategory = this.activeDemandTab
      }
      return params
    },

    // 加载测试数据 - 字典数据
    loadMockDictData() {
      this.marketCategoryDictData = [
        { dictCode: 'yAchievementTypev2', itemCode: '1', itemValue: '种质资源', parentCode: null, sortNum: 1 },
        { dictCode: 'yAchievementTypev2', itemCode: '2', itemValue: '新品种', parentCode: '1', sortNum: 1 },
        { dictCode: 'yAchievementTypev2', itemCode: '3', itemValue: '新技术', parentCode: null, sortNum: 2 },
        { dictCode: 'yAchievementTypev2', itemCode: '4', itemValue: '新装备', parentCode: null, sortNum: 3 },
        { dictCode: 'yAchievementTypev2', itemCode: '5', itemValue: '新产品', parentCode: null, sortNum: 4 }
      ]
      this.buildCategoryTabs()
    },

    // 加载成果列表（供需中心首页-成果超市）
    async loadAchievementList() {
      try {
        const params = {
          ...this.getMarketCategoryQueryParams(),
          num: 9
        }

        const response = await supplyDemandApi.getAchievementMarket(params)
        const result = response.data || {}
        if (result.status === 0) {
          const list = result.data || []

          this.achievementList = list.map(item => {
            // 图片
            let imageUrl = localeImg('default.png')
            if (item.fileList && item.fileList.length > 0) {
              const firstImage = item.fileList[0]
              if (firstImage.fileId) {
                imageUrl = supplyDemandApi.getImageUrl(firstImage.fileId)
              }
            }

            // 费用显示
            const feeType = String(item.transferFeeType ?? '')
            let priceText = '双方协商'
            if (feeType === '1') {
              priceText = item.transferFeeTypeLabel || '双方协商'
            } else if (item.transferFeeMin != null && item.transferFeeMax != null) {
              priceText = `${item.transferFeeMin}-${item.transferFeeMax}万元`
            } else if (item.transferFeeTypeLabel) {
              priceText = item.transferFeeTypeLabel
            }

            return {
              id: item.id,
              title: item.achievementName || '',
              transferType: item.transformMethodLabel || '',
              price: priceText,
              organization: item.unitPublic ? (item.unitName || '') : '',
              phone: item.phonePublic ? (item.contactPhone || '') : '',
              image: imageUrl
            }
          })
        } else {
          // 接口返回失败，使用测试数据
          this.loadMockAchievementList()
        }
      } catch (error) {
        console.error('加载成果超市数据失败:', error)
        // 接口异常，使用测试数据
        this.loadMockAchievementList()
      }
    },

    // 加载测试数据 - 成果列表
    loadMockAchievementList() {
      const mockData = [
        {
          id: '1',
          title: '优质小麦新品种培育技术',
          transferType: '技术转让',
          price: '50-100万元',
          organization: '西北农林科技大学',
          phone: '029-87080000',
          image: localeImg('park.png')
        },
        {
          id: '2',
          title: '智能农业物联网系统',
          transferType: '技术许可',
          price: '双方协商',
          organization: '杨凌示范区农业科技公司',
          phone: '029-87090000',
          image: localeImg('park.png')
        },
        {
          id: '3',
          title: '高效节水灌溉技术',
          transferType: '合作开发',
          price: '30-80万元',
          organization: '陕西省农业科学院',
          phone: '029-87070000',
          image: localeImg('park.png')
        },
        {
          id: '4',
          title: '有机肥料生产技术',
          transferType: '技术转让',
          price: '20-50万元',
          organization: '杨凌农业高新技术产业示范区',
          phone: '029-87060000',
          image: localeImg('park.png')
        },
        {
          id: '5',
          title: '农产品质量安全检测技术',
          transferType: '技术服务',
          price: '双方协商',
          organization: '中国农业科学院',
          phone: '010-82100000',
          image: localeImg('park.png')
        },
        {
          id: '6',
          title: '农业机器人智能采摘系统',
          transferType: '技术许可',
          price: '100-200万元',
          organization: '西安交通大学',
          phone: '029-82660000',
          image: localeImg('park.png')
        },
        {
          id: '7',
          title: '新型生物农药研发技术',
          transferType: '合作开发',
          price: '80-150万元',
          organization: '西北农林科技大学',
          phone: '029-87080000',
          image: localeImg('park.png')
        },
        {
          id: '8',
          title: '智慧农业大数据平台',
          transferType: '技术转让',
          price: '150-300万元',
          organization: '杨凌示范区科技公司',
          phone: '029-87090000',
          image: localeImg('park.png')
        },
        {
          id: '9',
          title: '设施农业环境控制系统',
          transferType: '技术服务',
          price: '双方协商',
          organization: '陕西省农业科学院',
          phone: '029-87070000',
          image: localeImg('park.png')
        }
      ]
      this.achievementList = mockData
    },

    // 加载需求大厅列表
    async loadDemandList() {
      try {
        const params = {
          ...this.getDemandCategoryQueryParams(),
          num: 8
        }

        const response = await supplyDemandApi.getRequirementHall(params)
        const result = response.data || {}
        if (result.status === 0 && Array.isArray(result.data)) {
          // 获取当前选中一级类别的标签
          const currentCategory = this.demandMainTabs.find(tab => tab.value === this.activeDemandMainTab)
          const categoryLabel = currentCategory ? currentCategory.label : '需求'
          
          this.demandList = result.data.map(item => {
            // 费用显示
            let priceText = '双方协商'
            const feeType = String(item.transferFeeType ?? '')
            if (feeType === '1') {
              priceText = item.transferFeeTypeLabel || '双方协商'
            } else if (item.transferFeeMin != null && item.transferFeeMax != null) {
              priceText = `${item.transferFeeMin}-${item.transferFeeMax}万元`
            } else if (item.transferFeeTypeLabel) {
              priceText = item.transferFeeTypeLabel
            }

            // 位置
            const location = [item.provinceLabel, item.cityLabel, item.districtLabel].filter(Boolean).join('')

            return {
              id: item.id,
              tag: item.firstCategoryLabel || categoryLabel,
              title: item.requirementName || '',
              price: priceText,
              organization: item.unitPublic ? (item.unitName || '') : '',
              location,
              deadline: item.deadline || '',
              // 合作方式：接口返回的 cooperationMethodLabel
              cooperationType: item.cooperationMethodLabel || ''
            }
          })
        } else {
          // 接口返回失败，使用测试数据
          this.loadMockDemandList()
        }
      } catch (error) {
        console.error('加载需求大厅数据失败:', error)
        // 接口异常，使用测试数据
        this.loadMockDemandList()
      }
    },

    // 加载测试数据 - 需求列表
    loadMockDemandList() {
      // 获取当前选中一级类别的标签
      const currentCategory = this.demandMainTabs.find(tab => tab.value === this.activeDemandMainTab)
      const categoryLabel = currentCategory ? currentCategory.label : '种质资源'
      
      const mockData = [
        {
          id: '1',
          tag: categoryLabel,
          title: '寻求优质小麦新品种合作开发',
          price: '50-100万元',
          organization: '陕西某农业科技公司',
          location: '陕西省西安市',
          deadline: '2025-12-31'
        },
        {
          id: '2',
          tag: categoryLabel,
          title: '急需智能农业物联网解决方案',
          price: '双方协商',
          organization: '杨凌示范区某企业',
          location: '陕西省杨凌示范区',
          deadline: '2025-11-30'
        },
        {
          id: '3',
          tag: categoryLabel,
          title: '合作开发高效节水灌溉技术',
          price: '30-80万元',
          organization: '陕西省某农业合作社',
          location: '陕西省渭南市',
          deadline: '2025-10-31'
        },
        {
          id: '4',
          tag: categoryLabel,
          title: '寻求有机肥料生产技术转让',
          price: '20-50万元',
          organization: '陕西省某生物科技公司',
          location: '陕西省宝鸡市',
          deadline: '2025-09-30'
        },
        {
          id: '5',
          tag: categoryLabel,
          title: '农产品质量安全检测技术服务',
          price: '双方协商',
          organization: '陕西省某食品公司',
          location: '陕西省咸阳市',
          deadline: '2025-08-31'
        },
        {
          id: '6',
          tag: categoryLabel,
          title: '农业机器人智能采摘系统合作',
          price: '100-200万元',
          organization: '陕西省某科技公司',
          location: '陕西省西安市',
          deadline: '2025-07-31'
        },
        {
          id: '7',
          tag: categoryLabel,
          title: '新型生物农药研发技术合作',
          price: '80-150万元',
          organization: '陕西省某化工企业',
          location: '陕西省汉中市',
          deadline: '2025-06-30'
        },
        {
          id: '8',
          tag: categoryLabel,
          title: '智慧农业大数据平台建设需求',
          price: '150-300万元',
          organization: '陕西省某信息技术公司',
          location: '陕西省西安市',
          deadline: '2025-05-31'
        }
      ]
      this.demandList = mockData
    },
    
    handleSearch() {
      const kw = String(this.searchKeyword || '').trim()
      if (!kw) return
      reportSearchIfKeyword(kw, 'achievement')
      this.$router.push({
        path: '/supply-demand/achievement-market',
        query: { keyword: kw }
      })
    },
    
    handleModuleClick(module) {
      this.currentMenu = module.id
      console.log('点击模块:', module.name)
      if (module.id === 'home') {
        this.$router.push('/index')
      }
      // 根据实际需要跳转
      // this.$router.push(module.link)
    },
    
    handleMoreClick(type) {
      console.log('查看更多:', type)
      // 跳转到对应列表页或详情页
      if (type === 'market') {
        // 成果超市 - 跳转到成果超市首页
        this.$router.push({
            path: '/supply-demand/achievement-market',
            query: { 
            }
          })
      } else if (type === 'demand') {
        this.$router.push({
          path: '/supply-demand/demand-hall'
        })
      } else if (type === 'jiebang') {
        this.$router.push({
          path: '/supply-demand/demand-hall-legacy',
          query: {
            type: 'jiebang'
          }
        })
      }
    },
    
    handleTabClick(type) {
      console.log('标签点击:', type)
    },
    
    handleItemClick(item) {
      console.log('项目点击:', item)
      // 根据类型跳转到不同的详情页
      const path = item.tag ? '/supply-demand/demand-detail' : '/supply-demand/detail'
      this.$router.push({
        path,
        query: { 
          id: item.id || '1',
          type: item.tag ? 'demand' : 'achievement'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.supply-demand-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

// 成果超市
.market-section {
  width: 1700px;
  margin: 0 auto 40px;
  margin-left: 110px;
}

// 需求大厅
.demand-section {
  width: 1700px;
  margin: 0 auto 40px;
  margin-left: 110px;
  
  .demand-list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    
    .demand-item {
      background: #FFFFFF;
      border-radius: 4px;
      padding: 0;
      
      cursor: pointer;
      transition: all 0.3s;
      position: relative;
      overflow: visible;
      border: 1px solid rgba(160, 144, 60, 0.2);
      display: flex;
      flex-direction: column;
      height: 100%;
      
      // 右下角装饰图片
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 170px;
        height: 168px;
        background-image: url('~@/assets/img/gongxuzhongxin/xuqiudating-item-bg-right-ico.png');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: right center;
        opacity: 0.15;
        transition: opacity 0.3s;
        pointer-events: none;
        z-index: 1;
      }
      
      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateY(-3px);
        border-color: rgba(160, 144, 60, 0.4);
        
        &::after {
          opacity: 0.3;
        }
      }
      
      .item-header {
        margin-bottom: 0;
        
        .item-tag {
          position: absolute;
          top: 0;
          left: 0;
              margin-left: 31px;
          min-width: 18px;
          height: 11px;
          background: #8C7B21;
          color: #FFFFFF;
          font-family: Source Han Sans CN;
          font-weight: 400;
          font-size: 12px;
          line-height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 10px;
          border-radius: 0 0 4px 0;
          z-index: 2;
        }
      }
      
      .item-title {
        font-family: Source Han Sans CN;
        font-weight: 500;
        font-size: 18px;
        color: #333333;
        margin-top: 20px;
        margin-left: 30px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        position: relative;
        z-index: 2;
      }
      
      .item-meta {
        margin-left: 30px;
        display: flex;
        margin-top: 8px;
        align-items: center;
        gap: 6px;
        margin-bottom: 10px;
        font-family: Source Han Sans CN;
        font-size: 12px;
        position: relative;
        z-index: 2;
        
        .meta-label {
          color: #887825;
        }
        
        .separator {
          color: #DDDDDD;
        }
        
        .meta-price {
          color: #666666;
          
          .highlight {
            color: #FF0000;
            font-weight: 500;
            margin-left: 2px;
          }
        }
      }
      
      .item-info {
        font-family: Source Han Sans CN;
        font-weight: 400;
        font-size: 12px;
        color: #777777;
        padding: 10px 30px 12px 30px;
        margin-top: auto;
        position: relative;
        background-image: url('~@/assets/img/gongxuzhongxin/xuqiudating-item-bg-bottom.png');
        background-repeat: no-repeat;
        background-position: bottom center;
        background-size: 100% auto;
        z-index: 2;
        
        .info-row {
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}


.supply-demand-page.locale-zh-CN {
  ::v-deep .category-tabs.main-tabs .tab-group .tab-item,
  ::v-deep .category-tabs.sub-tabs .sub-tabs-container .tab-group .tab-item {
    font-size: 18px;
  }
}
</style>
